import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { insightsApi, CloseSummary as CloseSummaryType } from '../api/insights';
import { formatCurrency, Currency } from '../utils/currencyUtils';
import { SparklesIcon, ExclamationTriangleIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

interface CloseSummaryProps {
  currency: Currency;
}

const CloseSummary: React.FC<CloseSummaryProps> = ({ currency }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [period, setPeriod] = useState<'weekly' | 'monthly' | 'quarterly'>('monthly');
  
  const { data: summary, isLoading } = useQuery<CloseSummaryType>({
    queryKey: ['close-summary', period],
    queryFn: () => insightsApi.getCloseSummary(period),
    refetchInterval: 300000, // Refresh every 5 minutes
  });

  if (isLoading) {
    return (
      <div className="premium-card p-6">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-200 border-t-indigo-600"></div>
        </div>
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  // Calculate summary stats for collapsed view
  const topClosesCount = summary.topCloses.length;
  const atRiskCount = summary.atRisk.length;
  const totalTopValue = summary.topCloses.reduce((sum, lead) => sum + lead.value, 0);

  return (
    <div className="premium-card overflow-hidden transition-all duration-300 hover:shadow-lg">
      {/* Header - Always Visible */}
      <div className="w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center flex-1">
            <div className="p-2.5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl mr-4 shadow-lg transition-shadow">
              <SparklesIcon className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 text-left">
              <h3 className="text-lg font-bold text-gray-900 mb-1">AI Close Summary</h3>
              <p className="text-sm text-gray-500">AI-powered close probability analysis</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {/* Period Dropdown */}
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as 'weekly' | 'monthly' | 'quarterly')}
              onClick={(e) => e.stopPropagation()}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-medium"
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
            {/* Toggle Button */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={`p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-all duration-200 ${isExpanded ? 'rotate-180' : ''}`}
            >
              {isExpanded ? (
                <ChevronUpIcon className="h-5 w-5 text-gray-600" />
              ) : (
                <ChevronDownIcon className="h-5 w-5 text-gray-600" />
              )}
            </button>
          </div>
        </div>
        {/* Quick Stats - Always Visible */}
        {!isExpanded && (
          <div className="flex items-center space-x-4 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span className="text-gray-600 font-medium">{topClosesCount} likely closes ({period})</span>
            </div>
            {atRiskCount > 0 && (
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                <span className="text-gray-600 font-medium">{atRiskCount} at risk</span>
              </div>
            )}
            {totalTopValue > 0 && (
              <div className="text-gray-600 font-semibold">
                {formatCurrency(totalTopValue, currency, { compact: true })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="px-6 pb-6 border-t border-gray-200 animate-in slide-in-from-top-2 duration-300">
          <div className="pt-6 space-y-6">

            {/* AI Summary Text */}
            {summary.summary && (
              <div className="p-5 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 rounded-xl border border-indigo-100 shadow-sm">
                <div className="flex items-start space-x-3">
                  <SparklesIcon className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">AI Analysis</h4>
                    <p className="text-sm text-gray-700 leading-relaxed">{summary.summary}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Top Likely Closes */}
              <div>
                <h4 className="text-sm font-bold text-gray-800 mb-4 flex items-center">
                  <div className="p-1.5 bg-green-100 rounded-lg mr-2">
                    <SparklesIcon className="h-4 w-4 text-green-600" />
                  </div>
                  Top 5 Likely Closes ({period.charAt(0).toUpperCase() + period.slice(1)})
                </h4>
                <div className="space-y-3">
                  {summary.topCloses.length > 0 ? (
                    summary.topCloses.map((lead, index) => (
                      <div
                        key={lead.lead_id}
                        className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              <span className="text-xs font-bold text-white bg-gradient-to-r from-green-500 to-emerald-600 px-2.5 py-1 rounded-full mr-2 shadow-sm">
                                #{index + 1}
                              </span>
                              <span className="text-sm font-semibold text-gray-900">
                                {lead.brand || lead.lead_name}
                                {lead.brand && lead.lead_name && (
                                  <span className="text-xs text-gray-500 ml-2 font-normal">• {lead.lead_name}</span>
                                )}
                              </span>
                            </div>
                            <div className="text-xs text-gray-700 space-y-1 mt-2">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium">Value:</span>
                                <span className="font-bold text-green-700">{formatCurrency(lead.value, currency)}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="font-medium">Probability:</span>
                                <span className="font-semibold">{lead.probability}%</span>
                                <span className="text-gray-400">•</span>
                                <span className="capitalize">{lead.stage.replace('_', ' ')}</span>
                              </div>
                              <div className="mt-2 pt-2 border-t border-green-200">
                                <span className="text-gray-600 italic">{lead.reason}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl text-center">
                      <p className="text-sm text-gray-500">No high-probability closes identified</p>
                    </div>
                  )}
                </div>
              </div>

              {/* At-Risk Deals */}
              <div>
                <h4 className="text-sm font-bold text-gray-800 mb-4 flex items-center">
                  <div className="p-1.5 bg-amber-100 rounded-lg mr-2">
                    <ExclamationTriangleIcon className="h-4 w-4 text-amber-600" />
                  </div>
                  At-Risk Deals
                </h4>
                <div className="space-y-3">
                  {summary.atRisk.length > 0 ? (
                    summary.atRisk.map((lead) => (
                      <div
                        key={lead.lead_id}
                        className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="text-sm font-semibold text-gray-900 mb-2">
                              {lead.brand || lead.lead_name}
                              {lead.brand && lead.lead_name && (
                                <span className="text-xs text-gray-500 ml-2 font-normal">• {lead.lead_name}</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-700 space-y-1">
                              <div className="flex items-center space-x-2">
                                <ExclamationTriangleIcon className="h-3 w-3 text-amber-600" />
                                <span>{lead.risk_reason}</span>
                              </div>
                              <div className="mt-2 pt-2 border-t border-amber-200">
                                <span className="text-gray-600">Days in stage: <span className="font-semibold">{lead.days_in_stage}</span></span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl text-center">
                      <p className="text-sm text-gray-500">No at-risk deals identified</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CloseSummary;
